import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import html2canvas from "html2canvas";
import axios from "axios";
import { X, Send, Bug, Sparkles, HelpCircle, Check, ExternalLink, Loader2, EyeOff } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { getMiraLogs, getMiraTelemetry, installMiraTelemetry } from "@/lib/miraTelemetry";

const API = `${window.location.origin}/api`;

function authHeaders() {
  const t = localStorage.getItem("auth_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const STATE_VERSION = "v2";

function storageKey(email) {
  return `mira_widget_state_${STATE_VERSION}:${(email || "anon").toLowerCase()}`;
}

const INTENTS = [
  { key: "bug", label: "Something broke", sub: "Report a bug", Icon: Bug, accent: "#DC2626" },
  { key: "feature", label: "I have an idea", sub: "Suggest a feature", Icon: Sparkles, accent: "#7C3AED" },
  { key: "question", label: "I have a question", sub: "Get help", Icon: HelpCircle, accent: "#0EA5E9" },
];

export default function MiraWidget() {
  const { user } = useAuth();
  const userEmail = user?.email || "";
  const key = useMemo(() => storageKey(userEmail), [userEmail]);

  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => localStorage.getItem("mira_hidden") === "1");
  const setHide = (v) => { setHidden(v); setOpen(false); localStorage.setItem("mira_hidden", v ? "1" : "0"); };
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // {role, text, chips?, summary?}
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [intent, setIntent] = useState(null);
  const [pendingSummary, setPendingSummary] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const widgetRootRef = useRef(null);
  const scrollerRef = useRef(null);

  // Install telemetry on first mount
  useEffect(() => { installMiraTelemetry(); }, []);

  // Restore draft per user
  useEffect(() => {
    if (!userEmail) return;
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const saved = JSON.parse(raw);
        setSessionId(saved.sessionId || null);
        setMessages(saved.messages || []);
        setIntent(saved.intent || null);
        setPendingSummary(saved.pendingSummary || null);
        setDraft(saved.draft || "");
      }
    } catch { /* ignore */ }
  }, [key, userEmail]);

  // Persist
  useEffect(() => {
    if (!userEmail) return;
    try {
      localStorage.setItem(key, JSON.stringify({
        sessionId, messages, intent, pendingSummary, draft,
      }));
    } catch { /* quota */ }
  }, [key, userEmail, sessionId, messages, intent, pendingSummary, draft]);

  // Auto-scroll
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, pendingSummary, submitted]);

  const captureScreenshot = useCallback(async (sid) => {
    try {
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        scale: Math.min(1, 1400 / window.innerWidth),
        logging: false,
        ignoreElements: (el) => el?.id === "mira-root" || el?.closest?.("#mira-root"),
        backgroundColor: "#ffffff",
      });
      const data = canvas.toDataURL("image/png", 0.85);
      await axios.post(`${API}/feedback/capture-screen`, {
        session_id: sid,
        image_base64: data,
        page_url: window.location.href,
      }, { headers: authHeaders() });
    } catch (e) {
      console.warn("[Mira] screenshot capture failed", e?.message || e);
    }
  }, []);

  const ensureSession = useCallback(async (payload = {}) => {
    if (sessionId) return sessionId;
    const resp = await axios.post(`${API}/feedback/start`, {
      page_url: window.location.href,
      ...payload,
    }, { headers: authHeaders() });
    const sid = resp.data.session_id;
    setSessionId(sid);
    if (resp.data.reply) {
      setMessages((m) => [...m, { role: "assistant", text: resp.data.reply, chips: resp.data.chips || [] }]);
    }
    if (resp.data.summary && resp.data.ready) setPendingSummary(resp.data.summary);
    captureScreenshot(sid);
    return sid;
  }, [sessionId, captureScreenshot]);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  // Ctrl/Cmd + M: bring Mira back (if hidden) and open her; again closes.
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey || e.key.toLowerCase() !== "m") return;
      e.preventDefault();
      setHidden(false);
      localStorage.setItem("mira_hidden", "0");
      setOpen((o) => !o);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const pickIntent = useCallback(async (intentKey) => {
    setIntent(intentKey);
    setSending(true);
    try {
      await ensureSession({ intent: intentKey });
    } catch (e) {
      console.error("[Mira] intent select failed", e);
    } finally {
      setSending(false);
    }
  }, [ensureSession]);

  const sendMessage = useCallback(async (text) => {
    const t = (text ?? draft).trim();
    if (!t || sending) return;
    setDraft("");
    setMessages((m) => [...m, { role: "user", text: t }]);
    setSending(true);
    try {
      let sid = sessionId;
      if (!sid) {
        // First message before any intent picked — start a free-form session
        sid = await ensureSession({ initial_message: t });
      } else {
        const resp = await axios.post(`${API}/feedback/chat`, {
          session_id: sid, message: t,
        }, { headers: authHeaders() });
        setMessages((m) => [...m, {
          role: "assistant",
          text: resp.data.reply,
          chips: resp.data.chips || [],
        }]);
        if (resp.data.summary && resp.data.ready) {
          setPendingSummary(resp.data.summary);
        }
      }
    } catch (e) {
      console.error("[Mira] chat failed", e);
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I lost connection. Try again?", chips: [] }]);
    } finally {
      setSending(false);
    }
  }, [draft, sessionId, sending, ensureSession]);

  const submitTicket = useCallback(async () => {
    if (!pendingSummary || !sessionId) return;
    setSubmitting(true);
    try {
      const resp = await axios.post(`${API}/feedback/submit`, {
        session_id: sessionId,
        triage: pendingSummary,
        page_url: window.location.href,
        telemetry: getMiraTelemetry(),
        logs: getMiraLogs(),
      }, { headers: authHeaders() });
      setSubmitted(resp.data);
    } catch (e) {
      console.error("[Mira] submit failed", e);
      setSubmitted({ ok: false, error: e?.response?.data?.detail || "Submit failed" });
    } finally {
      setSubmitting(false);
    }
  }, [pendingSummary, sessionId]);

  const resetSession = useCallback(() => {
    setSessionId(null);
    setMessages([]);
    setIntent(null);
    setPendingSummary(null);
    setSubmitted(null);
    setDraft("");
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  }, [key]);

  // Only render for authenticated users
  if (!user) return null;

  if (hidden) {
    return (
      <div id="mira-root" ref={widgetRootRef}>
        <button onClick={() => setHide(false)} title="Show Mira" aria-label="Show Mira" data-testid="mira-show"
                className="fixed bottom-6 right-0 z-[10000] h-8 w-2 hover:w-6 rounded-l-md bg-slate-300/70 hover:bg-slate-400 transition-[width,background-color] duration-150" />
      </div>
    );
  }

  return (
    <div id="mira-root" ref={widgetRootRef}>
      <BubbleButton open={open} onClick={open ? () => setOpen(false) : handleOpen} onHide={() => setHide(true)} />
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0.22, 0.9, 0.36, 1] }}
            className="fixed bottom-14 right-6 z-[10000] w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-7rem)] flex flex-col overflow-hidden rounded-2xl bg-white shadow-[0_24px_60px_-12px_rgba(0,0,0,0.35)] border border-slate-200"
            data-testid="mira-drawer"
          >
            <Header onClose={() => setOpen(false)} onHide={() => setHide(true)} onReset={resetSession} hasSession={!!sessionId && !submitted} />
            <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-[#FAFAF9]">
              {submitted ? (
                <Submitted submitted={submitted} onNew={resetSession} />
              ) : (
                <>
                  {messages.length === 0 && !sending && (
                    <div className="bg-white border border-slate-200 rounded-2xl p-3 text-[13px] text-slate-700 leading-relaxed" data-testid="mira-greeting">
                      Hey, I&apos;m <span className="font-semibold text-slate-900">Mira</span>. I&apos;ll get the right info to the team in 30 seconds. Pick the option that fits or just tell me what&apos;s going on.
                    </div>
                  )}
                  {messages.map((m, i) => (
                    <Message
                      key={i}
                      role={m.role}
                      text={m.text}
                      chips={m.chips}
                      isLast={i === messages.length - 1 && !sending}
                      onChip={(c) => sendMessage(c)}
                    />
                  ))}
                  {!intent && messages.length <= 1 && !pendingSummary && (
                    <IntentCards onPick={pickIntent} disabled={sending} />
                  )}
                  {sending && <TypingDots />}
                  {pendingSummary && !submitting && (
                    <ReviewCard summary={pendingSummary} onSubmit={submitTicket} onEdit={() => setPendingSummary(null)} />
                  )}
                  {submitting && <SubmittingState />}
                </>
              )}
            </div>
            {!submitted && (
              <Composer
                value={draft}
                onChange={setDraft}
                onSend={() => sendMessage()}
                disabled={sending || submitting}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function BubbleButton({ open, onClick, onHide }) {
  return (
    <div className="group fixed bottom-6 right-6 z-[10000]">
      <motion.button
        onClick={onClick}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="h-7 w-7 rounded-full overflow-hidden shadow-[0_5px_15px_-3px_rgba(0,0,0,0.35)] ring-1 ring-white bg-white flex items-center justify-center"
        aria-label={open ? "Close Mira" : "Open Mira"}
        data-testid="mira-bubble"
      >
        {open ? (
          <X className="h-3.5 w-3.5 text-slate-800" />
        ) : (
          <img src="/marfi-logo.jpeg" alt="Mira" className="h-full w-full object-cover" />
        )}
      </motion.button>
      {!open && (
        <button onClick={onHide} title="Hide Mira" aria-label="Hide Mira" data-testid="mira-hide-bubble"
                className="absolute -top-2 -left-2 h-4 w-4 rounded-full bg-slate-700 text-white grid place-items-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity">
          <X className="h-2.5 w-2.5" />
        </button>
      )}
    </div>
  );
}

function Header({ onClose, onHide, onReset, hasSession }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-white">
      <img src="/marfi-logo.jpeg" alt="MARFI" className="h-8 w-8 rounded-full object-cover" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-slate-900 leading-tight">Mira</div>
        <div className="text-[11px] text-slate-500 leading-tight">by MARFI · AI Support</div>
      </div>
      {hasSession && (
        <button
          onClick={onReset}
          className="text-[11px] text-slate-500 hover:text-slate-900 px-2 py-1 rounded-md hover:bg-slate-100"
          data-testid="mira-reset"
        >
          New chat
        </button>
      )}
      <button
        onClick={onHide}
        className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-900 px-2 py-1 rounded-md hover:bg-slate-100"
        title="Hide Mira — click the tab at the right edge to bring her back"
        data-testid="mira-hide"
      >
        <EyeOff className="h-3.5 w-3.5" /> Hide
      </button>
      <button
        onClick={onClose}
        className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600"
        aria-label="Close"
        data-testid="mira-close"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function IntentCards({ onPick, disabled }) {
  return (
    <div className="space-y-2 pt-1" data-testid="mira-intent-cards">
      {INTENTS.map(({ key, label, sub, Icon, accent }) => (
        <button
          key={key}
          disabled={disabled}
          onClick={() => onPick(key)}
          className="group w-full flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all text-left disabled:opacity-50"
          data-testid={`mira-intent-${key}`}
        >
          <span
            className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${accent}14`, color: accent }}
          >
            <Icon className="h-4 w-4" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-semibold text-slate-900 leading-tight">{label}</span>
            <span className="block text-[11px] text-slate-500 leading-tight mt-0.5">{sub}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

function Message({ role, text, chips, isLast, onChip }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className="max-w-[85%]">
        <div
          className={`px-3 py-2 rounded-2xl text-[13px] leading-relaxed whitespace-pre-wrap ${
            isUser
              ? "bg-[#0F172A] text-white rounded-br-sm"
              : "bg-white text-slate-900 border border-slate-200 rounded-bl-sm"
          }`}
          data-testid={isUser ? "mira-msg-user" : "mira-msg-assistant"}
        >
          {text}
        </div>
        {!isUser && isLast && chips && chips.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid="mira-chips">
            {chips.map((c, i) => (
              <button
                key={i}
                onClick={() => onChip(c)}
                className="text-[11px] px-2.5 py-1 rounded-full bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 hover:border-slate-400 transition-colors"
                data-testid={`mira-chip-${i}`}
              >
                {c}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 px-3 py-2 w-fit rounded-2xl bg-white border border-slate-200" data-testid="mira-typing">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="h-1.5 w-1.5 rounded-full bg-slate-400"
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1, repeat: Infinity, delay: i * 0.15 }}
        />
      ))}
    </div>
  );
}

function ReviewCard({ summary, onSubmit, onEdit }) {
  const sev = summary.severity || "low";
  const sevColors = {
    critical: "bg-red-100 text-red-700",
    high: "bg-orange-100 text-orange-700",
    medium: "bg-amber-100 text-amber-700",
    low: "bg-sky-100 text-sky-700",
  }[sev] || "bg-slate-100 text-slate-700";
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-2" data-testid="mira-review">
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Ready to file</span>
        <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${sevColors}`}>{sev}</span>
      </div>
      <div className="text-sm font-semibold text-slate-900 leading-tight">{summary.title}</div>
      <div className="text-[12px] text-slate-600 leading-snug line-clamp-3">{summary.description}</div>
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={onSubmit}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-[#0F172A] text-white rounded-lg hover:bg-[#1e293b] transition-colors"
          data-testid="mira-submit"
        >
          <Check className="h-3.5 w-3.5" /> Send to team
        </button>
        <button
          onClick={onEdit}
          className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-100"
          data-testid="mira-edit"
        >
          Add more
        </button>
      </div>
    </div>
  );
}

function SubmittingState() {
  return (
    <div className="flex items-center gap-2 text-xs text-slate-500 px-2" data-testid="mira-submitting">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      Filing your ticket…
    </div>
  );
}

function Submitted({ submitted, onNew }) {
  if (submitted?.ok === false) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2 text-center" data-testid="mira-submitted-error">
        <div className="text-sm font-semibold text-red-900">Couldn&apos;t file ticket</div>
        <div className="text-[12px] text-red-700">{submitted.error || "Unknown error"}</div>
        <button onClick={onNew} className="text-xs font-semibold text-slate-900 underline mt-2">Try again</button>
      </div>
    );
  }
  const gh = submitted?.github || {};
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 text-center" data-testid="mira-submitted">
      <div className="h-10 w-10 mx-auto rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
        <Check className="h-5 w-5" />
      </div>
      <div className="text-sm font-semibold text-slate-900">Sent! The team has it.</div>
      <div className="text-[12px] text-slate-500">
        {submitted.github_ok ? "GitHub issue filed" : "GitHub: skipped"}{" · "}
        {submitted.discord_ok ? "Discord notified" : "Discord: skipped"}
      </div>
      {gh.url && (
        <a
          href={gh.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-[12px] text-slate-700 hover:text-slate-900 underline"
          data-testid="mira-github-link"
        >
          View issue #{gh.number} <ExternalLink className="h-3 w-3" />
        </a>
      )}
      <div className="pt-1">
        <button
          onClick={onNew}
          className="text-xs font-semibold text-slate-900 hover:underline"
          data-testid="mira-new-chat"
        >
          Start a new chat
        </button>
      </div>
    </div>
  );
}

function Composer({ value, onChange, onSend, disabled }) {
  return (
    <div className="border-t border-slate-200 bg-white p-2 flex items-end gap-2">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSend();
          }
        }}
        rows={1}
        placeholder="Type a message…"
        className="flex-1 resize-none text-[13px] px-3 py-2 rounded-lg bg-slate-100 focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-300 placeholder:text-slate-400 max-h-24"
        disabled={disabled}
        data-testid="mira-input"
      />
      <button
        onClick={onSend}
        disabled={disabled || !value.trim()}
        className="h-9 w-9 rounded-lg bg-[#0F172A] text-white flex items-center justify-center hover:bg-[#1e293b] disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
        aria-label="Send"
        data-testid="mira-send"
      >
        <Send className="h-4 w-4" />
      </button>
    </div>
  );
}
